class StickyBottomBar {
    constructor() {
        this.stickyBar = document.getElementById('sticky-bottom-bar');
        if (!this.stickyBar) return;

        this.form = this.stickyBar.querySelector('form');
        this.submitButton = this.stickyBar.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('.sticky-bottom-bar__button-text');
        this.variantIdInput = this.form?.querySelector('[name="id"]');
        this.deliveryDateInput = this.form?.querySelector('[name="properties[Delivery Date]"]');
        this.priceElement = this.stickyBar.querySelector('.sticky-bottom-bar__price');
        this.comparePriceElement = this.stickyBar.querySelector('.sticky-bottom-bar__compare-price');

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        // Scroll behavior variables
        this.isVisible = false;

        const scrollThresholdFactor = this.stickyBar.dataset.scrollThresholdFactor
          ? Number(this.stickyBar.dataset.scrollThresholdFactor)
          : 2;

        this.scrollThreshold = window.innerHeight * scrollThresholdFactor; // Show after scrolling past two viewports

        if (this.stickyBar.dataset.exactThreshold) {
            this.scrollThreshold = Number(this.stickyBar.dataset.exactThreshold);
        }

        this.lastScrollY = 0;
        this.ticking = false;

        this.init();
    }

    init() {
        if (!this.stickyBar || !this.form) return;

        // Set up scroll listener
        this.setupScrollListener();

        // Set up form submission
        this.setupFormSubmission();

        // Listen to variant changes from main product form
        this.setupVariantChangeListener();

        // Set up delivery date synchronization
        this.setupDeliveryDateSync();

        // Initial display state
        this.updateVisibility();
    }

    setupScrollListener() {
        const requestTick = () => {
            if (!this.ticking) {
                requestAnimationFrame(() => {
                    this.updateVisibility();
                    this.ticking = false;
                });
                this.ticking = true;
            }
        };

        window.addEventListener('scroll', requestTick, { passive: true });
        window.addEventListener('resize', () => {
            this.scrollThreshold = window.innerHeight * 2;
            if (this.stickyBar.dataset.exactThreshold) {
                this.scrollThreshold = Number(this.stickyBar.dataset.exactThreshold);
            }
            requestTick();
        }, { passive: true });
    }

    updateVisibility() {
        // Only show on mobile (match CSS media query)
        if (window.innerWidth >= 750) {
            this.hide();
            return;
        }

        const currentScrollY = window.scrollY;
        const shouldShow = currentScrollY > this.scrollThreshold;

        if (shouldShow && !this.isVisible) {
            this.show();
        } else if (!shouldShow && this.isVisible) {
            this.hide();
        }

        this.lastScrollY = currentScrollY;
    }

    show() {
        if (!this.stickyBar) return;

        this.stickyBar.style.display = 'block';
        // Force reflow to ensure display change takes effect
        this.stickyBar.offsetHeight;
        this.stickyBar.classList.add('show');
        this.isVisible = true;
    }

    hide() {
        if (!this.stickyBar) return;

        this.stickyBar.classList.remove('show');
        this.isVisible = false;

        // Hide completely after transition
        setTimeout(() => {
            if (!this.isVisible) {
                this.stickyBar.style.display = 'none';
            }
        }, 300);
    }

    setupFormSubmission() {
        if (!this.form || !this.submitButton) return;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        if (document.querySelector('cart-drawer')) {
            this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
    }

    onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const spinner = this.submitButton.querySelector('.loading__spinner');
        if (spinner) {
            spinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        const { 'Content-Type': contentType, ...headers } = config.headers;
        config.headers = headers;

        const formData = new FormData(this.form);

        if (this.cart) {
            formData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
                if (response.status) {
                    // Handle error
                    publish(PUB_SUB_EVENTS.cartError, {
                        source: 'sticky-bottom-bar',
                        productVariantId: formData.get('id'),
                        errors: response.errors || response.description,
                        message: response.message,
                    });

                    this.handleError(response.description);
                    return;
                }

                if (!this.cart) {
                    window.location = window.routes.cart_url;
                    return;
                }

                // Success - update cart
                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'sticky-bottom-bar',
                    productVariantId: formData.get('id'),
                    cartData: response,
                });

                this.cart.renderContents(response);
            })
            .catch((e) => {
                console.error('Sticky bottom bar cart error:', e);
                this.handleError('An error occurred while adding to cart');
            })
            .finally(() => {
                this.submitButton.classList.remove('loading');
                if (this.cart?.classList.contains('is-empty')) {
                    this.cart.classList.remove('is-empty');
                }
                this.submitButton.removeAttribute('aria-disabled');

                const spinner = this.submitButton?.querySelector('.loading__spinner');
                spinner?.classList.add('hidden');
            });
    }

    handleError(errorMessage) {
        // For now, just log the error. Could show a toast or update button text
        console.error('Sticky bottom bar error:', errorMessage);

        // Optionally update button text temporarily
        if (this.submitButtonText) {
            const originalText = this.submitButtonText.textContent;
            this.submitButtonText.textContent = 'Error occurred';

            setTimeout(() => {
                this.submitButtonText.textContent = originalText;
            }, 3000);
        }
    }

    setupVariantChangeListener() {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            this.updateVariant(event);
        });
    }

    updateVariant(event) {
        if (!event.data.variant) return;

        const variant = event.data.variant;

        // Update hidden variant ID input
        if (this.variantIdInput) {
            this.variantIdInput.value = variant.id;
            this.variantIdInput.disabled = !variant.available;
        }

        // Update pricing
        this.updatePricing(variant);

        // Update button state
        this.updateButtonState(variant);
    }

    updatePricing(variant) {
        if (this.priceElement) {
            this.priceElement.textContent = formatMoney(variant.price, window.theme.moneyFormat);
        }

        if (this.comparePriceElement) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                this.comparePriceElement.textContent = formatMoney(variant.compare_at_price, window.theme.moneyFormat);
                this.comparePriceElement.style.display = 'inline';
            } else {
                this.comparePriceElement.style.display = 'none';
            }
        }
    }

    updateButtonState(variant) {
        if (!this.submitButton || !this.submitButtonText) return;

        if (!variant.available) {
            this.submitButton.setAttribute('disabled', 'disabled');
            this.submitButtonText.textContent = window.variantStrings.soldOut || 'Sold out';
        } else {
            this.submitButton.removeAttribute('disabled');
            this.submitButtonText.textContent = window.variantStrings.addToCart || 'Add to cart';
        }
    }

    setupDeliveryDateSync() {
        // Sync delivery date on initialization
        this.syncDeliveryDate();

        // Listen for delivery date changes on the main product form
        const deliveryDateInputs = document.querySelectorAll('input[name="properties[Delivery Date]"]:not(.delivery-date-input)');
        deliveryDateInputs.forEach(input => {
            input.addEventListener('change', () => {
                this.syncDeliveryDate();
            });
        });
    }

    syncDeliveryDate() {
        if (!this.deliveryDateInput) return;

        // Try to get the selected delivery date from the main product form
        const selectedDeliveryDate = document.querySelector('input[name="properties[Delivery Date]"]:not(.delivery-date-input):checked');

        if (selectedDeliveryDate) {
            this.deliveryDateInput.value = selectedDeliveryDate.value;
        } else {
            // Fallback to default value if nothing is selected
            this.deliveryDateInput.value = 'October 10';
        }
    }

    destroy() {
        if (this.variantChangeUnsubscriber) {
            this.variantChangeUnsubscriber();
        }
    }
}

// Helper function to format money (basic version)
function formatMoney(centsParam, format) {
    let cents = centsParam;
    if (typeof cents !== 'number') {
        cents = Number.parseInt(cents, 10);
    }

    const value = (cents / 100).toFixed(2);

    if (format) {
        return format.replace('{{amount}}', value);
    }

    return `$${value}`;
}

// Initialize the sticky bottom bar when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    const stickyBottomBar = document.getElementById('sticky-bottom-bar');
    if (stickyBottomBar) {
        new StickyBottomBar();
    }
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
    // DOM is still loading
} else {
    // DOM is already loaded
    const stickyBottomBar = document.getElementById('sticky-bottom-bar');
    if (stickyBottomBar) {
        new StickyBottomBar();
    }
}